import React, { useRef, useState } from 'react';
import AuthModal from '../elements/authModal.jsx';
import { fetchMe } from '../elements/authApi.jsx';
import { createDesign, uploadDesignFiles } from '../elements/designsApi.jsx';
import { IconClose, IconUpload, IconCheck } from './hudIcons.jsx';
import { FILM_ANGLES, DEFAULT_FILM_ANGLES } from './filmRecorder.jsx';

// پنلِ «ارسال فایل روی سرور» (ذخیره‌ی طرحِ رندرشده — نه رندر گرفتن).
// prepare(setStep) → { meta, files: { نام: Blob } } — فایل‌ها را آماده می‌کند (یک بار؛ برای «تلاشِ دوباره» نگه
// داشته می‌شود). onSaved(design) بعد از موفقیت.

const fa = (n, d = 0) => Number(n).toLocaleString('fa-IR', { maximumFractionDigits: d });
const faMB = (bytes) => fa(bytes / (1024 * 1024), 1);
const STEP_TEXT = {
  lightmap: 'آماده‌سازیِ نقشه‌ی نور…',
  model: 'آماده‌سازیِ مدلِ سه‌بعدی…',
  compress: 'فشرده‌سازی…',
  thumb: 'گرفتنِ عکسِ طرح…',
  plan: 'آماده‌سازیِ نقشه…',
  photos: 'آماده‌سازیِ عکس‌های آگهی…',
  film: 'ساختِ فیلمِ طرح…',
};

function defaultTitle() {
  return `طرحِ ${new Date().toLocaleDateString('fa-IR', { month: 'long', day: 'numeric' })}`;
}

// چک‌لیستِ نماهای فیلم — کاربر انتخاب می‌کند فیلم از کدام زاویه‌ها ساخته شود
function AnglePicker({ angles, onToggle, onSet }) {
  const all = FILM_ANGLES.map((a) => a.id);
  return (
    <div className="create-3d__angles">
      <div className="create-3d__angles-head">
        <span>نماهای فیلم ({fa(angles.length)} از {fa(FILM_ANGLES.length)})</span>
        <button type="button" onClick={() => onSet(angles.length === all.length ? DEFAULT_FILM_ANGLES : all)}>
          {angles.length === all.length ? 'پیش‌فرض' : 'همه'}
        </button>
      </div>
      <div className="create-3d__angles-list">
        {FILM_ANGLES.map((a) => (
          <label key={a.id} className={angles.includes(a.id) ? 'is-on' : ''}>
            <input type="checkbox" checked={angles.includes(a.id)} onChange={() => onToggle(a.id)} />
            <i aria-hidden="true">{angles.includes(a.id) ? '✓' : ''}</i>
            <span>
              {a.label}
              <small>{a.hint}</small>
            </span>
          </label>
        ))}
      </div>
      {!angles.length && <p className="create-3d__angles-warn">حداقل یک نما را انتخاب کنید</p>}
    </div>
  );
}

// mode='finish' («پایان طراحی»): فقط فایل‌ها و فیلمِ تبلیغاتی ساخته می‌شوند و onPrepared(prepared) صدا زده می‌شود —
// چیزی روی سرور نمی‌رود؛ انتشار بعداً از صفحه‌ی «ساخت فایل جدید» (publishDesign.jsx)
export default function SaveDesignSheet({ prepare, onClose, onSaved, mode = 'upload', onPrepared }) {
  const finishMode = mode === 'finish';
  const [title, setTitle] = useState(defaultTitle);
  const [stage, setStage] = useState('form'); // form | auth | preparing | uploading | done | error
  const [step, setStep] = useState(null);
  const [progress, setProgress] = useState({ sent: 0, total: 0 });
  const [error, setError] = useState('');
  const prepared = useRef(null);
  const designId = useRef(null);
  // پیش‌نمایشِ زنده‌ی ضبطِ فیلم: بومِ ضبط همین‌جا دیده می‌شود
  const filmBox = useRef(null);
  const [filmPct, setFilmPct] = useState(0);
  // نماهای فیلم (چک‌لیست): پیش‌فرض همان نماهای قبلی؛ آخرین انتخابِ کاربر روی همین گوشی می‌ماند
  const [angles, setAngles] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('kh-film-angles') || 'null');
      if (Array.isArray(saved) && saved.length) return saved.filter((id) => FILM_ANGLES.some((a) => a.id === id));
    } catch (e) {
      /* */
    }
    return DEFAULT_FILM_ANGLES;
  });
  const toggleAngle = (id) =>
    setAngles((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try {
        localStorage.setItem('kh-film-angles', JSON.stringify(next));
      } catch (e) {
        /* */
      }
      return next;
    });
  const filmUi = {
    angles,
    onFilmCanvas: (canvas) => {
      if (filmBox.current) {
        filmBox.current.innerHTML = '';
        filmBox.current.appendChild(canvas);
      }
    },
    onFilmProgress: setFilmPct,
  };
  const busy = stage === 'preparing' || stage === 'uploading';

  const run = async () => {
    setError('');
    try {
      if (!prepared.current) {
        setStage('preparing');
        prepared.current = await prepare(setStep, filmUi);
      }
      if (finishMode) {
        setStage('done');
        if (onPrepared) onPrepared(prepared.current);
        return;
      }
      const { meta, files } = prepared.current;
      setStage('uploading');
      if (!designId.current) {
        const created = await createDesign({ ...meta, title }, files);
        designId.current = created.id;
      }
      const { design } = await uploadDesignFiles(designId.current, files, (sent, total) => setProgress({ sent, total }));
      setStage('done');
      onSaved && onSaved(design);
    } catch (e) {
      console.error('[saveDesign]', e); // eslint-disable-line no-console
      if (e.code === 'not-logged-in') {
        setStage('auth');
        return;
      }
      // طرحِ خراب/منقضی: دفعه‌ی بعد از اول ساخته شود
      if (['not-found', 'not-uploading', 'bad-model', 'bad-image', 'bad-plan', 'bad-file'].includes(e.code)) designId.current = null;
      if (e.message === 'not-finished') prepared.current = null;
      setError(e.code ? e.message : `آماده‌سازیِ فایل‌ها ناموفق بود (${String(e.message || e).slice(0, 80)})`);
      setStage('error');
    }
  };

  const start = async () => {
    if (finishMode) {
      run();
      return;
    }
    try {
      const me = await fetchMe();
      if (!me) {
        setStage('auth');
        return;
      }
    } catch (e) {
      setError(e.message);
      setStage('error');
      return;
    }
    run();
  };

  const pct = progress.total ? progress.sent / progress.total : 0;

  return (
    <>
      <div className="create-3d__sheet create-3d__save" onPointerDown={(e) => e.stopPropagation()}>
        <div className="create-3d__sheet-head">
          <div>
            <strong>{finishMode ? 'پایان طراحی' : 'ارسال فایل روی سرور'}</strong>
          </div>
          <button type="button" onClick={onClose} aria-label="بستن" disabled={busy}>
            <IconClose size={16} />
          </button>
        </div>

        {finishMode && stage !== 'done' ? (
          <>
            <p className="create-3d__sheet-note">
              فیلمِ تبلیغاتیِ خانه ساخته می‌شود و به صفحه‌ی آگهی برمی‌گردید. هنوز چیزی منتشر نمی‌شود — آن‌جا فیلم را
              می‌بینید و با «انتشار» آگهی روی سایت می‌رود.
            </p>
            {!busy && stage !== 'error' && <AnglePicker angles={angles} onToggle={toggleAngle} onSet={setAngles} />}
            {error && <p className="create-3d__sheet-error">{error}</p>}
            <div className={`create-3d__save-film ${stage === 'preparing' && step === 'film' ? 'is-on' : ''}`} ref={filmBox} />
            {busy ? (
              <div className="create-3d__sheet-progress">
                <div className="create-3d__hud-bar is-indeterminate">
                  <i />
                </div>
                <span>
                  {step === 'film' ? `${STEP_TEXT.film} ${fa(filmPct * 100)}٪` : STEP_TEXT[step] || 'آماده‌سازیِ فایل‌ها…'}
                </span>
              </div>
            ) : (
              <button type="button" className="create-3d__render-start is-primary" onClick={stage === 'error' ? run : start} disabled={!angles.length}>
                <IconCheck size={18} strokeWidth={2.2} />
                <span>{stage === 'error' ? 'تلاشِ دوباره' : 'ساختِ فیلم و پایان طراحی'}</span>
              </button>
            )}
            {busy && <p className="create-3d__sheet-note">فیلمِ خانه در حالِ ضبط است — صفحه را خاموش یا عوض نکنید.</p>}
          </>
        ) : finishMode ? (
          <div className="create-3d__save-done">
            <span className="create-3d__save-done-icon">
              <IconCheck size={26} strokeWidth={2.4} />
            </span>
            <strong>طراحی تمام شد</strong>
          </div>
        ) : stage === 'done' ? (
          <div className="create-3d__save-done">
            <span className="create-3d__save-done-icon">
              <IconCheck size={26} strokeWidth={2.4} />
            </span>
            <strong>طرح ذخیره شد</strong>
            <p>«{title}» روی سرور ذخیره شد و در پروفایلِ شما دیده می‌شود. این نام فقط برای خودتان است.</p>
            <button type="button" className="create-3d__render-start is-primary" onClick={onClose}>
              <span>باشه</span>
            </button>
          </div>
        ) : (
          <>
            <p className="create-3d__sheet-note">
              نقشه‌ی طرح (برای ویرایشِ بعدی) و نسخه‌ی رندرشده‌ی آن، فشرده‌شده، روی سرور ذخیره می‌شود. همه‌ی طرح‌ها عمومی‌اند.
            </p>
            <div className="create-3d__sheet-label">نامِ طرح (خصوصی)</div>
            <input
              className="create-3d__sheet-link"
              type="text"
              maxLength={60}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy || Boolean(designId.current)}
              placeholder="مثلاً آپارتمانِ ۸۵ متری"
            />
            <p className="create-3d__save-private">این نام فقط برای خودتان نمایش داده می‌شود و کسِ دیگری آن را نمی‌بیند.</p>
            {!busy && stage !== 'error' && !prepared.current && <AnglePicker angles={angles} onToggle={toggleAngle} onSet={setAngles} />}

            {error && <p className="create-3d__sheet-error">{error}</p>}

            <div className={`create-3d__save-film ${stage === 'preparing' && step === 'film' ? 'is-on' : ''}`} ref={filmBox} />
            {busy ? (
              <div className="create-3d__sheet-progress">
                <div className={`create-3d__hud-bar ${stage === 'preparing' ? 'is-indeterminate' : ''}`}>
                  <i style={stage === 'uploading' ? { width: `${pct * 100}%`, animation: 'none', left: 0 } : undefined} />
                </div>
                <span>
                  {stage === 'preparing'
                    ? step === 'film'
                      ? `${STEP_TEXT.film} ${fa(filmPct * 100)}٪`
                      : STEP_TEXT[step] || 'آماده‌سازیِ فایل‌ها…'
                    : progress.total
                      ? `ارسال — ${fa(pct * 100)}٪ (${faMB(progress.sent)} از ${faMB(progress.total)} مگابایت)`
                      : 'ارسال…'}
                </span>
              </div>
            ) : (
              <button type="button" className="create-3d__render-start is-primary" onClick={stage === 'error' ? run : start}>
                <IconUpload size={18} />
                <span>{stage === 'error' ? 'تلاشِ دوباره' : 'ارسال فایل روی سرور'}</span>
              </button>
            )}
            {busy && (
              <p className="create-3d__sheet-note">
                {step === 'film' && stage === 'preparing'
                  ? 'فیلمِ کوتاهِ طرح برای پروفایل ضبط می‌شود — صفحه را خاموش یا عوض نکنید.'
                  : 'تا پایانِ ارسال این صفحه را نبندید.'}
              </p>
            )}
          </>
        )}
      </div>

      {stage === 'auth' && (
        <AuthModal
          onClose={() => setStage('form')}
          onSuccess={() => {
            setStage('form');
            run();
          }}
        />
      )}
    </>
  );
}
